import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Zap, Activity } from 'lucide-react';
import apiClient from '@/api';
import Navbar from '@/components/Navbar';

const AVATAR_EMOJIS = ['🌱', '🌿', '🪴', '🌸', '🌺', '🌻', '✨', '🌟'];

export default function Leaderboard({ user: currentUser }) {
  const [groupRankings, setGroupRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await apiClient.get('/leaderboard');
        setGroupRankings(response.data);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  // Logic to find current user's rank dynamically
  const rankIndex = groupRankings.findIndex(u => u.id === currentUser?.id);
  const userRank = rankIndex >= 0 ? rankIndex + 1 : '??';
  const rankPercent = rankIndex >= 0 && groupRankings.length > 0
    ? ((rankIndex + 1) / groupRankings.length * 100).toFixed(0)
    : '--';
  const displayPoints = groupRankings[rankIndex]?.points ?? currentUser?.points ?? 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] text-[#5C4A42] p-4 flex items-center justify-center">
        <p className="text-[#8B7355] uppercase tracking-widest text-sm">Loading leaderboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F3EF] text-[#5C4A42] selection:bg-[#D4A574] selection:text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto space-y-4 p-4">

        <header className="mb-8 pt-6">
          <h1 className="text-4xl md:text-6xl font-black uppercase italic tracking-tighter">
            Global <span className="text-[#D4A574]">Rankings</span>
          </h1>
          <p className="font-mono text-[10px] text-[#8B7355] uppercase tracking-[0.3em] mt-2">// Season 01: The Awakening</p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-4 md:auto-rows-[180px] gap-4">

          {/* TILE A: TACTICAL PODIUM (Enhanced with Avatars) */}
          <div className="min-h-[220px] md:min-h-0 md:col-span-2 md:row-span-2 rounded-3xl warm-card p-6 flex items-end justify-around gap-2 relative overflow-hidden">
            <div className="absolute top-4 left-6 flex items-center gap-2">
               <Trophy size={14} className="text-[#D4A574]" />
               <span className="text-[10px] font-black uppercase tracking-widest text-[#A89284]">Global Top 3</span>
            </div>

            {groupRankings?.slice(0, 3).map((user, idx) => {
              // Map heights based on rank: 1st is tallest
              const heights = ["h-32", "h-24", "h-16"];
              const order = ["order-2", "order-1", "order-3"]; // 1st in middle

              return (
                <motion.div
                  key={user.id}
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`flex flex-col items-center ${order[idx]} w-1/3`}
                >
                  <div className="text-3xl mb-2 drop-shadow-[0_0_10px_rgba(212,165,116,0.5)]">
                    {AVATAR_EMOJIS[(user.avatar_stage || 1) - 1]}
                  </div>
                  <div className={`${heights[idx]} bg-[#FAF9F7] border-t-2 ${idx === 0 ? 'border-[#D4A574]' : 'border-[#D4C4B0]'} w-full rounded-t-xl relative flex flex-col items-center justify-end pb-4 transition-all hover:bg-[#F5F3EF]`}>
                    <span className="text-[10px] font-black uppercase text-[#5C4A42] truncate w-full text-center px-1">
                      {user.name}
                    </span>
                    <span className="text-[8px] font-mono text-[#D4A574] font-bold">
                      {user.points} XP
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* TILE B: YOUR STATUS (Dynamic & High Contrast) */}
          <motion.div
            whileHover={{ scale: 0.98 }}
            className="md:col-span-1 md:row-span-1 rounded-3xl bg-[#D4A574] p-6 text-white flex flex-col justify-between"
          >
            <div className="flex justify-between items-start">
              <span className="text-[10px] font-black uppercase tracking-tighter">Your Position</span>
              <Activity size={16} />
            </div>
            <div>
              <div className="text-6xl font-black italic tracking-tighter">#{userRank.toString().padStart(2, '0')}</div>
              <p className="text-[10px] font-bold uppercase mt-1">Top {rankPercent}% of Athletes</p>
            </div>
          </motion.div>

          {/* TILE C: SEASON DATA */}
          <div className="md:col-span-1 md:row-span-1 rounded-3xl warm-card p-6 flex flex-col justify-between">
            <Zap className="text-[#D4A574]" size={20} />
            <div>
              <span className="text-[10px] font-bold text-[#A89284] uppercase tracking-widest">Season Points</span>
              <div className="text-3xl font-black italic tracking-tight">
                {displayPoints.toLocaleString()}
              </div>
            </div>
          </div>

          {/* TILE D: TELEMETRY LIST (Scrollable & Tactical) */}
          <div className="max-h-[420px] md:max-h-none md:col-span-2 md:row-span-2 rounded-3xl warm-card flex flex-col overflow-hidden">
            <div className="p-4 border-b border-[#D4C4B0] bg-[#FAF9F7] flex justify-between items-center">
              <span className="text-[10px] font-black uppercase tracking-widest text-[#A89284]">Athlete Registry</span>
              <span className="text-[10px] font-mono text-[#A89284]">TOTAL_RECORDS: {groupRankings?.length}</span>
            </div>
            <div className="overflow-y-auto flex-1 p-2 custom-scrollbar">
              {groupRankings.length === 0 && (
                <p className="text-center text-[#A89284] text-xs uppercase tracking-widest py-8">No athletes ranked yet</p>
              )}
              {groupRankings.map((user, idx) => (
                <motion.div
                  key={user.id}
                  initial={{ x: -10, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  className="flex items-center justify-between p-3 rounded-xl hover:bg-[#FAF9F7] group transition-all"
                >
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-[#A89284] text-xs w-4">{idx + 1}</span>
                    <span className="text-xl">{AVATAR_EMOJIS[(user.avatar_stage || 1) - 1]}</span>
                    <div>
                      <span className="font-black uppercase text-xs tracking-tight group-hover:text-[#D4A574] transition-colors">
                        {user.name}
                      </span>
                      <div className="text-[8px] font-bold text-[#A89284] uppercase">Level {user.level || 1}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-black text-[#D4A574] font-mono text-sm">{user.points?.toLocaleString()}</span>
                    <span className="text-[8px] font-bold text-[#A89284] ml-1 uppercase">XP</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
